import type { Vec2, Vec3 } from '../geo/types';
import type { BoatState, InputFrame, Pose } from './types';
import { ahead, type BoatWater } from '../nav/boatWater';
import { BOAT, PLAYER, WORLD } from '../core/config';
import { clamp, damp } from '../core/math';
import { layoutToWorld } from '../core/coords';

/** Bearings tried, to either side of the one asked for, when a boat is put on the water. */
const LAUNCH_TURNS = 8;
/** How far out from the nearest water a berth is looked for, in metres. */
const LAUNCH_REACH_M = [0, 1, 2, 3, 5, 8];

/** Where along and across the hull the water's height is read, in metres from its middle. */
const RIDE_ALONG_M = [-2, -1, 0, 1, 2];
const RIDE_ACROSS_M = [-0.6, 0, 0.6];

/** How a hull lies on moving water: lifted, and tilted about its two level axes. Its own frame
 *  has the bow at -X, as its mesh does. */
export interface BoatRide {
  /** Metres over the still water. */
  readonly heave: number;
  /** Radians about the hull's +Z: positive lifts the stern. */
  readonly pitch: number;
  /** Radians about the hull's +X: positive drops its +Z side. */
  readonly roll: number;
}

export const LEVEL_RIDE: BoatRide = { heave: 0, pitch: 0, roll: 0 };

/** The ride of a hull on the water under it: the plane that best fits the water's height over
 *  the hull's own plan, so waves shorter than the boat pass under it and longer ones carry it. */
export function boatRide(
  boat: Pick<BoatState, 'position' | 'bearing'>,
  swellAt: (p: Vec2) => number
): BoatRide {
  const turn = boat.bearing - Math.PI / 2;
  const [cos, sin] = [Math.cos(turn), Math.sin(turn)];
  let [sum, alongSum, acrossSum, alongSquares, acrossSquares] = [0, 0, 0, 0, 0];
  for (const x of RIDE_ALONG_M) {
    for (const z of RIDE_ACROSS_M) {
      const height = swellAt([
        boat.position[0] + x * cos + z * sin,
        boat.position[1] + x * sin - z * cos,
      ]);
      sum += height;
      alongSum += x * height;
      acrossSum += z * height;
      alongSquares += x * x;
      acrossSquares += z * z;
    }
  }
  return {
    heave: sum / (RIDE_ALONG_M.length * RIDE_ACROSS_M.length),
    pitch: Math.atan(alongSum / alongSquares),
    roll: -Math.atan(acrossSum / acrossSquares),
  };
}

export interface BoatMotion {
  readonly boat: BoatState;
  readonly pose: Pose;
}

/** The eye of the visitor on the aft bench. */
export function boatEye(boat: Pick<BoatState, 'position' | 'bearing'>): Vec3 {
  const [x, y] = ahead(boat.bearing);
  return layoutToWorld(
    [boat.position[0] - x * BOAT.benchAftM, boat.position[1] - y * BOAT.benchAftM],
    WORLD.waterY + BOAT.eyeHeight
  );
}

/** A boat lying still where it is. */
export function moored(boat: BoatState): BoatState {
  return { ...boat, speed: 0, turn: 0 };
}

/* The bearings to either side of the one asked for, nearest first. */
function bearingsFrom(bearing: number): readonly number[] {
  return Array.from({ length: 2 * LAUNCH_TURNS }, (_, k) => {
    const turn = Math.ceil(k / 2) * (k % 2 === 0 ? -1 : 1);
    return bearing + (turn * Math.PI) / LAUNCH_TURNS;
  });
}

/** A boat on the water nearest a point, heading as near the asked bearing as its berth allows.
 *  A berth with room to turn the boat right round is taken before one hard against a quay.
 *  Null where no water near the point has room for a hull. */
export function launch(water: BoatWater, near: Vec2, bearing: number): BoatState | null {
  const shore = water.nearest(near);
  const gap = Math.hypot(shore[0] - near[0], shore[1] - near[1]);
  const out: Vec2 = gap > 0 ? [(shore[0] - near[0]) / gap, (shore[1] - near[1]) / gap] : [0, 0];
  const bearings = bearingsFrom(bearing);
  const berths = LAUNCH_REACH_M.map((reach): Vec2 => [
    shore[0] + out[0] * reach,
    shore[1] + out[1] * reach,
  ]);
  const roomy = berths.find((position) => bearings.every((b) => water.floats(position, b)));
  if (roomy) return { position: roomy, bearing, speed: 0, turn: 0 };
  for (const position of berths) {
    const fits = bearings.find((b) => water.floats(position, b));
    if (fits !== undefined) return { position, bearing: fits, speed: 0, turn: 0 };
  }
  return null;
}

function targetSpeed(input: InputFrame): number {
  if (input.forward > 0) return input.boost ? BOAT.pullSpeed : BOAT.rowSpeed;
  return input.forward < 0 ? -BOAT.backSpeed : 0;
}

/** One frame at the oars. The boat keeps its way and turns about its own middle; the visitor's
 *  view turns with it and stays free to look around. */
export function updateBoat(
  boat: BoatState,
  pose: Pose,
  input: InputFrame,
  dt: number,
  water: BoatWater,
  swellAt?: (p: Vec2) => number
): BoatMotion {
  const wanted = damp(boat.turn, -input.right * BOAT.turnRate, BOAT.turnTime, dt);
  const swung = boat.bearing + wanted * dt;
  // A bow or stern that would swing into a bank or a hull stops the turn, not the boat.
  const turns = water.floats(boat.position, swung);
  const bearing = turns ? swung : boat.bearing;

  const wantedSpeed = damp(boat.speed, targetSpeed(input), BOAT.accelTime, dt);
  const [x, y] = ahead(bearing);
  const step = wantedSpeed * dt;
  const position = water.advance(boat.position, bearing, [x * step, y * step]);
  // What the boat met takes its way off it: its speed is the way it actually made.
  const made = (position[0] - boat.position[0]) * x + (position[1] - boat.position[1]) * y;
  const speed = dt > 0 && Math.abs(made) < Math.abs(step) ? made / dt : wantedSpeed;

  const next: BoatState = { position, bearing, speed, turn: turns ? wanted : 0 };
  const yaw = pose.yaw - input.lookDx * PLAYER.lookRadPerPx + (bearing - boat.bearing);
  // The bench rides with the hull: it rises with the stern, and the horizon tips as the boat rolls.
  const ride = swellAt ? boatRide(next, swellAt) : LEVEL_RIDE;
  const eye = boatEye(next);
  return {
    boat: next,
    pose: {
      position: [eye[0], eye[1] + ride.heave + Math.tan(ride.pitch) * BOAT.benchAftM, eye[2]],
      yaw,
      pitch: clamp(
        pose.pitch - input.lookDy * PLAYER.lookRadPerPx,
        -PLAYER.pitchLimit,
        PLAYER.pitchLimit
      ),
      roll: ride.roll * Math.cos(yaw - bearing),
      fovYDeg: pose.fovYDeg,
    },
  };
}
